import { useEffect, useRef, useState } from 'react';

import { Html, Line } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';

interface ScalePickOverlayProps {
  active: boolean;
  /** Called when the user has clicked two points. Receives the two points and their Euclidean distance in model units. */
  onTwoPointsPicked: (p1: THREE.Vector3, p2: THREE.Vector3, distanceUnits: number) => void;
  /** Called when the user presses Escape mid-pick. */
  onCancel?: () => void;
}

/**
 * Raycasts against the scene to let a user pick two points on the uploaded model.
 * Renders small markers + a connecting line + a live distance label.
 * Only active while `active === true`.
 */
export function ScalePickOverlay({ active, onTwoPointsPicked, onCancel }: ScalePickOverlayProps) {
  const { gl, camera, scene } = useThree();
  const [points, setPoints] = useState<THREE.Vector3[]>([]);
  const [cursor, setCursor] = useState<THREE.Vector3 | null>(null);
  const raycaster = useRef(new THREE.Raycaster());

  // Reset on deactivation
  useEffect(() => {
    if (!active) {
      setPoints([]);
      setCursor(null);
    }
  }, [active]);

  useEffect(() => {
    if (!active) return;
    const el = gl.domElement;

    // Raycast helper — returns world-space point under pointer, or null.
    // Defined inside the effect so it closes over the current gl/camera/scene
    // without needing to be listed as an extra dep.
    const pickPoint = (clientX: number, clientY: number): THREE.Vector3 | null => {
      const rect = el.getBoundingClientRect();
      const x = ((clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.current.setFromCamera(new THREE.Vector2(x, y), camera);
      const targets: THREE.Object3D[] = [];
      scene.traverse((o) => {
        if (o instanceof THREE.Mesh && o.visible && !o.userData.isHelper && !o.userData.scalePickIgnore) {
          targets.push(o);
        }
      });
      const hits = raycaster.current.intersectObjects(targets, false);
      return hits.length > 0 ? hits[0].point.clone() : null;
    };

    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 0) return;
      const p = pickPoint(e.clientX, e.clientY);
      if (!p) return;
      e.stopPropagation();
      e.preventDefault();
      setPoints((prev) => {
        const next = [...prev, p];
        if (next.length === 2) {
          const dist = next[0].distanceTo(next[1]);
          // Defer to avoid setState during event
          setTimeout(() => onTwoPointsPicked(next[0], next[1], dist), 0);
        }
        return next;
      });
    };

    const onPointerMove = (e: PointerEvent) => {
      const p = pickPoint(e.clientX, e.clientY);
      setCursor(p);
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onCancel) onCancel();
    };

    // Capture phase so we run before OrbitControls (but OrbitControls is disabled via `enabled` prop anyway)
    el.addEventListener('pointerdown', onPointerDown, true);
    el.addEventListener('pointermove', onPointerMove);
    window.addEventListener('keydown', onKey);
    el.style.cursor = 'crosshair';
    return () => {
      el.removeEventListener('pointerdown', onPointerDown, true);
      el.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('keydown', onKey);
      el.style.cursor = '';
    };
  }, [active, gl, camera, scene, onTwoPointsPicked, onCancel]);

  if (!active) return null;

  const p1 = points[0];
  const p2 = points[1] ?? (points.length === 1 ? cursor : null);
  const showLine = p1 && p2;
  const distance = showLine ? p1!.distanceTo(p2!) : 0;

  return (
    <group userData={{ scalePickIgnore: true }}>
      {points.map((p, i) => (
        <mesh key={i} position={[p.x, p.y, p.z]} userData={{ scalePickIgnore: true }}>
          <sphereGeometry args={[0.15, 16, 16]} />
          <meshBasicMaterial color="#d4ff33" depthTest={false} transparent opacity={0.95} />
        </mesh>
      ))}
      {points.length === 1 && cursor && (
        <mesh position={[cursor.x, cursor.y, cursor.z]} userData={{ scalePickIgnore: true }}>
          <sphereGeometry args={[0.1, 12, 12]} />
          <meshBasicMaterial color="#d4ff33" depthTest={false} transparent opacity={0.5} />
        </mesh>
      )}
      {showLine && (
        <>
          <Line
            points={[
              [p1!.x, p1!.y, p1!.z],
              [p2!.x, p2!.y, p2!.z],
            ]}
            color="#d4ff33"
            lineWidth={2}
            transparent
            depthTest={false}
          />
          <Html
            position={[(p1!.x + p2!.x) / 2, (p1!.y + p2!.y) / 2, (p1!.z + p2!.z) / 2]}
            center
            style={{ pointerEvents: 'none' }}
          >
            <div
              style={{
                background: 'rgba(212,255,51,0.9)',
                color: '#000',
                padding: '2px 6px',
                borderRadius: 4,
                fontSize: 11,
                fontWeight: 600,
                fontFamily: 'monospace',
                whiteSpace: 'nowrap',
              }}
            >
              {distance.toFixed(3)} u
            </div>
          </Html>
        </>
      )}
    </group>
  );
}
